// ENG-2105: the client-side mirror of the backend's dedicated-slot rule
// (suno_backend/scheduling/business_logic/dedicated_slot_enforcement.py). The backend is the
// authority; this exists so the form can explain the rule before Save instead of after a 400.
import { format } from "date-fns";

import type { AppointmentBlock } from "services/types/events.type";

export const DEDICATED_SLOT_RESERVED = "dedicated_slot_reserved";

export interface DedicatedSlotType {
  id: number;
  name: string;
  color?: string;
  // "Allow other appointment types to book during this type's blocks". Availability treats a slot
  // made only of such types as open time, so enforcement does too.
  allowOtherTypesDuringBlock?: boolean;
}

export interface DedicatedSlot {
  id: number | string;
  // null => a clinic-wide slot, which reserves the time for every provider at that clinic.
  staffMemberId: number | null;
  clinicId?: number | null;
  start: Date;
  end: Date;
  appointmentTypes: DedicatedSlotType[];
}

export interface BookingWindow {
  staffMemberId: number | null | undefined;
  clinicId?: number | null;
  start: Date;
  end: Date;
  appointmentTypeId: number | null | undefined;
}

export const slotReservesTime = (slot: DedicatedSlot): boolean =>
  slot.appointmentTypes.length > 0 &&
  !slot.appointmentTypes.every((type) => type.allowOtherTypesDuringBlock);

const overlaps = (slot: DedicatedSlot, window: BookingWindow): boolean =>
  window.start < slot.end && slot.start < window.end;

// The backend only looks at slots at the booking's own clinic. An unknown clinic on either side
// (a form with no clinic chosen yet) keeps the slot, so the warning errs towards showing.
const sameClinic = (slot: DedicatedSlot, window: BookingWindow): boolean =>
  slot.clinicId == null ||
  window.clinicId == null ||
  slot.clinicId === window.clinicId;

const appliesToProvider = (
  slot: DedicatedSlot,
  window: BookingWindow
): boolean =>
  sameClinic(slot, window) &&
  (slot.staffMemberId === null || slot.staffMemberId === window.staffMemberId);

/** True when one of the slot's types is the booking's type. */
export const slotAcceptsType = (
  types: Pick<DedicatedSlotType, "id">[],
  appointmentTypeId: number | null | undefined
): boolean => types.some((type) => type.id === appointmentTypeId);

/** Slots that truly overlap the booking and apply to its provider, whatever their types. */
export const findOverlappingSlots = (
  slots: DedicatedSlot[],
  window: BookingWindow
): DedicatedSlot[] =>
  slots.filter(
    (slot) => appliesToProvider(slot, window) && overlaps(slot, window)
  );

/**
 * The legacy scheduler's pre-ENG-2105 overlap test, kept for the enforcement-off path so it
 * stays unchanged: the booking's start OR end falls inside the slot (edges excluded) and the slot
 * is the same provider's. Clinic-wide and team slots arrive as one copy per staff member, so they
 * match through the copy's id, as on master; a slot with no staff member at all never matches.
 * It misses a booking that fully covers a slot.
 */
export const legacyBlockIntersects = (
  slot: DedicatedSlot,
  window: BookingWindow
): boolean => {
  if (slot.staffMemberId === null) return false;
  if (slot.staffMemberId !== window.staffMemberId) return false;
  const inside = (moment: Date) => slot.start <= moment && moment <= slot.end;
  return (
    (inside(window.start) && window.start.getTime() !== slot.end.getTime()) ||
    (inside(window.end) && window.end.getTime() !== slot.start.getTime())
  );
};

type Span = { start: Date; end: Date };

/** What is left of `span` once every `cut` is removed from it. */
const subtractSpans = (span: Span, cuts: Span[]): Span[] =>
  cuts.reduce<Span[]>(
    (pieces, cut) =>
      pieces.flatMap((piece) =>
        cut.end <= piece.start || cut.start >= piece.end
          ? [piece]
          : [
              ...(cut.start > piece.start
                ? [{ start: piece.start, end: cut.start }]
                : []),
              ...(cut.end < piece.end
                ? [{ start: cut.end, end: piece.end }]
                : [])
            ]
      ),
    [span]
  );

/**
 * Slots that reserve part of the booking against its type. Empty => the booking is allowed.
 * As the backend does, the overlapping slots that DO take the type are carved out of the ones
 * that don't first, so a matching slot wins where it overlaps a non-matching one.
 */
export const findReservingSlots = (
  slots: DedicatedSlot[],
  window: BookingWindow
): DedicatedSlot[] => {
  if (window.appointmentTypeId == null) return [];
  const relevant = slots.filter(
    (slot) => appliesToProvider(slot, window) && overlaps(slot, window)
  );
  const matching = relevant.filter((slot) =>
    slotAcceptsType(slot.appointmentTypes, window.appointmentTypeId)
  );
  return relevant.filter(
    (slot) =>
      slotReservesTime(slot) &&
      !slotAcceptsType(slot.appointmentTypes, window.appointmentTypeId) &&
      subtractSpans(slot, matching).some((piece) =>
        overlaps({ ...slot, ...piece }, window)
      )
  );
};

/** Unique type names across the reserving slots, in slot order. */
export const reservedTypeNames = (slots: DedicatedSlot[]): string[] => [
  ...new Set(
    slots.flatMap((slot) => slot.appointmentTypes.map((type) => type.name))
  )
];

/** ["A"] => "A"; ["A","B"] => "A or B"; ["A","B","C"] => "A, B or C". */
export const joinTypeNames = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
};

// ENG-2174: the backend's refusal ends with the same sentence.
export const DEDICATED_SLOT_ASK_FOR_OVERRIDE =
  "Ask someone with slot-override access to book a different type here.";

export const reservedSlotHeadline = (
  timeRange: string,
  typeNames: string[]
): string =>
  `${timeRange} is inside a slot reserved for ${joinTypeNames(typeNames)}.`;

export const reservedSlotNextStep = `Choose one of those types or another time. ${DEDICATED_SLOT_ASK_FOR_OVERRIDE}`;

/** ENG-2154: the second line for a user who may book over the slot; the checkbox follows it. */
export const reservedSlotOverrideNextStep =
  "Choose one of those types or another time, or tick below to book it anyway.";

/**
 * "10:00–10:45 AM" when both ends share AM/PM, else "11:30 AM–12:15 PM". 12-hour, no leading
 * zero, en dash — the backend formats its refusal message the same way.
 */
export const formatSlotTimeRange = (start: Date, end: Date): string => {
  if (format(start, "a") === format(end, "a")) {
    return `${format(start, "h:mm")}\u2013${format(end, "h:mm a")}`;
  }
  return `${format(start, "h:mm a")}\u2013${format(end, "h:mm a")}`;
};

// A drag is saved without opening the form, so its refusal needs to say the move did not happen.
export const DEDICATED_SLOT_NOT_MOVED_PREFIX = "Not moved. ";

/** Prefix dedicated-slot refusals from a drag with "Not moved. "; other errors pass through. */
export const withNotMovedPrefix = <T extends { code: string; message: string }>(
  errors: T[]
): T[] =>
  errors.map((error) =>
    error.code === DEDICATED_SLOT_RESERVED
      ? {
          ...error,
          message: `${DEDICATED_SLOT_NOT_MOVED_PREFIX}${error.message}`
        }
      : error
  );

/** The form values the backend's dedicated-slot check keys on. */
export interface BookingFieldValues {
  id?: number | string | null;
  appointment_type?: { id: number } | null;
  appointment_date?: Date | string | null;
  start_time?: Date | string | null;
  end_time?: Date | string | null;
  staff_member?: { id: number } | null;
  clinic?: { id: number } | null;
}

// The form keeps the day and the clock time in separate fields; join them to the minute.
const momentOf = (
  date: Date | string | null | undefined,
  time: Date | string | null | undefined
): number | null => {
  if (!date || !time) return null;
  const day = new Date(date);
  const clock = new Date(time);
  if (Number.isNaN(day.getTime()) || Number.isNaN(clock.getTime())) return null;
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    clock.getHours(),
    clock.getMinutes()
  ).getTime();
};

/** True when the type, start, end, provider or clinic differs from what the form loaded. */
export const bookingFieldsChanged = (
  loaded: BookingFieldValues,
  current: BookingFieldValues
): boolean =>
  loaded.appointment_type?.id !== current.appointment_type?.id ||
  loaded.staff_member?.id !== current.staff_member?.id ||
  loaded.clinic?.id !== current.clinic?.id ||
  momentOf(loaded.appointment_date, loaded.start_time) !==
    momentOf(current.appointment_date, current.start_time) ||
  momentOf(loaded.appointment_date, loaded.end_time) !==
    momentOf(current.appointment_date, current.end_time);

/**
 * Mirrors when the backend runs the check: always on create, and on update only when one of the
 * booking fields changed. An existing appointment already inside another type's slot stays
 * saveable while none of them change.
 */
export const slotCheckApplies = (
  loaded: BookingFieldValues | null | undefined,
  current: BookingFieldValues
): boolean => !loaded?.id || bookingFieldsChanged(loaded, current);

/**
 * Whether a reserved slot disables Save on an appointment. A user who can override clears it
 * with the checkbox; one who cannot is blocked only when the backend would check the booking.
 */
export const reservedSlotBlocksSave = ({
  isReserved,
  mustRespect,
  overwriteBlock,
  checkApplies
}: {
  isReserved: boolean;
  mustRespect: boolean;
  overwriteBlock: boolean | undefined;
  checkApplies: boolean;
}): boolean => {
  if (!isReserved) return false;
  return mustRespect ? checkApplies : !overwriteBlock;
};

/**
 * A loaded legacy Block event as the rule sees it. events-optimized returns a clinic-wide or team
 * slot as one copy per staff member, each with that member's id, so the copy's id is kept; null
 * only when the payload genuinely carries no staff member.
 */
export const blockToDedicatedSlot = (
  block: AppointmentBlock
): DedicatedSlot => {
  const types =
    block.appointmentTypes ??
    (block.appointmentType ? [block.appointmentType] : []);
  return {
    id: block.id,
    staffMemberId: Number.isFinite(block.staffMemberId)
      ? block.staffMemberId
      : null,
    clinicId: block.clinicId,
    start: new Date(block.startDate),
    end: new Date(block.endDate),
    appointmentTypes: types.map((type) => ({
      id: type.id,
      name: type.name,
      color: type.color,
      // Sent by AppointmentTypeTinySerializer; a slot made only of such types is open time.
      allowOtherTypesDuringBlock: type.allow_other_types_during_block
    }))
  };
};

/**
 * The slots that would refuse a dragged appointment at its drop target: the target's provider,
 * clinic and times, the appointment's own type, true overlap.
 */
export const reservingSlotsForMove = (
  slots: DedicatedSlot[],
  target: BookingWindow
): DedicatedSlot[] => findReservingSlots(slots, target);

/** The move-dialog warning for a drop target, or null when the target takes the type. */
export const reservedSlotMoveWarning = (
  slots: DedicatedSlot[],
  target: BookingWindow
): string | null => {
  const reserving = reservingSlotsForMove(slots, target);
  if (reserving.length === 0) return null;
  return reservedSlotHeadline(
    formatSlotTimeRange(target.start, target.end),
    reservedTypeNames(reserving)
  );
};

/**
 * The drag confirm dialog's warning for a legacy drop: shown to anyone while enforcement is on
 * (override users' moves go through, front desk's are refused after Yes), never while it is off.
 */
export const dragReservedSlotWarning = ({
  isEnforced,
  blocks,
  target
}: {
  isEnforced: boolean;
  blocks: AppointmentBlock[];
  target: BookingWindow;
}): string | null =>
  isEnforced
    ? reservedSlotMoveWarning(blocks.map(blockToDedicatedSlot), target)
    : null;
